import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { hostname, isSafeUrl } from "../../../utils";

interface GmailIntegrationFormProps {
  editMode: boolean;
  initialData?: {
    composioConnectedAccountId?: string;
    connectedAt?: string;
    email?: string;
  };
  companyId: string;
  instanceName: string;
  getToken: () => Promise<string>;
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: any) => void;
}

/**
 * Gmail OAuth form component using Composio's managed OAuth.
 *
 * The flow:
 * 1. User clicks "Connect Gmail Account"
 * 2. Backend initiates OAuth with Composio for Gmail
 * 3. Popup opens for user to authorize with Google
 * 4. On popup close, we check if connection was successful
 * 5. We fetch the Gmail profile to get the email address
 * 6. User provides inbox name and clicks Install
 */
export default function GmailIntegrationForm({
  editMode,
  initialData = {},
  companyId,
  instanceName,
  getToken,
  onValidationChange,
  onDataChange,
}: GmailIntegrationFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [stateToken, setStateToken] = useState<string | null>(null);
  const [composioData, setComposioData] = useState<{
    composioConnectedAccountId?: string;
    composioToolkit?: string;
  }>({});

  // Gmail profile data
  const [email, setEmail] = useState("");
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  // Inbox name
  const [inboxName, setInboxName] = useState("");

  // Check if already connected (edit mode)
  useEffect(() => {
    if (editMode && initialData.composioConnectedAccountId) {
      setIsConnected(true);
      if (initialData.email) {
        setEmail(initialData.email);
        setInboxName(initialData.email);
      }
    }
  }, [editMode, initialData.composioConnectedAccountId, initialData.email]);

  // Determine if form is valid - need connection and inbox name
  const isFormValid = isConnected && email.length > 0 && inboxName.trim().length > 0;

  // Update parent when state changes
  useEffect(() => {
    onValidationChange(isFormValid);
    onDataChange({
      connected: isConnected,
      stateToken,
      email,
      inboxName: inboxName.trim(),
      ...composioData,
    });
  }, [isFormValid, isConnected, stateToken, composioData, email, inboxName, onValidationChange, onDataChange]);

  // Fetch Gmail profile after OAuth connection
  const fetchGmailProfile = useCallback(
    async (connectedAccountId: string) => {
      setIsLoadingProfile(true);
      setError("");

      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/integrations/gmail/profile`,
          {
            params: {
              composioConnectedAccountId: connectedAccountId,
            },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.profile?.email) {
          const emailAddr = response.data.profile.email;
          setEmail(emailAddr);
          // Default inbox name to email
          if (!inboxName) {
            setInboxName(emailAddr);
          }
        } else {
          setError("Could not retrieve Gmail profile. Please try reconnecting.");
        }
      } catch (err: any) {
        console.error("Error fetching Gmail profile:", err);
        setError(
          err.response?.data?.error ||
            "Failed to fetch Gmail profile. Please try reconnecting."
        );
      } finally {
        setIsLoadingProfile(false);
      }
    },
    [getToken, inboxName]
  );

  // Handle OAuth flow
  const handleConnect = useCallback(async () => {
    setIsLoading(true);
    setError("");
    setEmail("");

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/integrations/composio/connect`,
        {
          companyId,
          integrationType: "gmail",
          instanceName: instanceName || "Gmail Account",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.redirectUrl) {
        // Store state token for completion
        setStateToken(response.data.stateToken);

        // Open OAuth in a popup window
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
        if (!safeUrlPattern.test(response.data.redirectUrl)) return;
        const popup = window.open(
          response.data.redirectUrl,
          "GmailOAuth",
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        // Poll for popup close and check connection status
        const pollInterval = setInterval(async () => {
          if (popup?.closed) {
            clearInterval(pollInterval);

            // Check if connection was successful
            try {
              const currentToken = await getToken();
              const statusResponse = await axios.get(
                `${hostname}/integrations/composio/status?companyId=${companyId}&integrationType=gmail`,
                {
                  headers: { Authorization: `Bearer ${currentToken}` },
                }
              );

              if (statusResponse.data.connected) {
                // Connection successful - get the active connection
                const connections = statusResponse.data.connections || [];
                const connectedAccount = connections.find(
                  (c: any) => c.status === "ACTIVE"
                );

                if (connectedAccount) {
                  // Store Composio data in state
                  setComposioData({
                    composioConnectedAccountId: connectedAccount.id,
                    composioToolkit: connectedAccount.toolkit,
                  });
                  setIsConnected(true);

                  // Fetch Gmail profile to get email address
                  await fetchGmailProfile(connectedAccount.id);
                } else {
                  setError("No active connection found. Please try again.");
                }
              } else {
                setError("Connection not completed. Please try again.");
              }
            } catch (err: any) {
              console.error("Error checking connection status:", err);
              setError(
                err.response?.data?.error ||
                  "Failed to verify connection. Please try again."
              );
            }

            setIsLoading(false);
          }
        }, 1000);

        // Check if popup was blocked
        if (!popup || popup.closed || typeof popup.closed === "undefined") {
          clearInterval(pollInterval);
          setError(
            "Popup was blocked. Please allow popups for this site and try again."
          );
          setIsLoading(false);
        }
      }
    } catch (err: any) {
      console.error("Error starting Gmail OAuth:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to start connection. Please try again."
      );
      setIsLoading(false);
    }
  }, [companyId, instanceName, getToken, fetchGmailProfile]);

  // In edit mode with existing connection, show connected status
  if (editMode && isConnected) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 2 }}>
          Gmail account connected
          {initialData.email && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Email: {initialData.email}
            </Typography>
          )}
          {initialData.connectedAt && (
            <Typography variant="caption" display="block">
              Connected on:{" "}
              {new Date(initialData.connectedAt).toLocaleDateString()}
            </Typography>
          )}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          To reconnect with a different account, remove this integration and
          create a new one.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Connect your Gmail account with Google OAuth. No app passwords or
          credentials required.
          <br /><br />
          <strong>Features:</strong>
          <ul style={{ margin: "8px 0", paddingLeft: 20 }}>
            <li>Receive and sync emails automatically</li>
            <li>Send emails and replies through AI Employees</li>
            <li>Works with personal Gmail and Google Workspace accounts</li>
          </ul>
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!isConnected ? (
        <>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={isLoading}
            startIcon={
              isLoading && <CircularProgress size={16} color="inherit" />
            }
            fullWidth
            size="large"
            sx={{ mb: 2 }}
          >
            {isLoading ? "Connecting..." : "Connect Gmail Account"}
          </Button>

          <Alert severity="warning">
            <Typography variant="caption">
              <strong>Note:</strong> A popup will open for you to sign in with
              Google and authorize access. Make sure pop-ups are not blocked.
            </Typography>
          </Alert>
        </>
      ) : (
        <>
          {isLoadingProfile ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
              <CircularProgress size={20} />
              <Typography>Loading Gmail profile...</Typography>
            </Box>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Gmail connected successfully!
                {email && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Email:</strong> {email}
                  </Typography>
                )}
              </Alert>

              <TextField
                fullWidth
                label="Inbox Name"
                value={inboxName}
                onChange={(e) => setInboxName(e.target.value)}
                helperText="A name to identify this inbox (e.g., 'Sales Gmail' or your email address)"
                required
                sx={{ mb: 2 }}
              />

              <Alert severity="info">
                <Typography variant="caption">
                  Click "Install" to create this Gmail inbox. Emails will sync
                  automatically every 5 minutes.
                </Typography>
              </Alert>
            </>
          )}
        </>
      )}
    </Box>
  );
}
